import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { or, componentWithName, htmlElement } from '@tds/util-prop-types';
import { borders, forms } from '@tds/shared-styles';
import { medium, boldFont } from '@tds/shared-typography';
import { colorPrimary, colorWhite, colorSecondary, colorText } from '@tds/core-colours';
import { media } from '@tds/core-responsive';
import { safeRest } from '@tds/util-helpers';

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var _extends_1 = createCommonjsModule(function (module) {
function _extends() {
  module.exports = _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

module.exports = _extends;
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

/* eslint-disable import/prefer-default-export */
var warn = function warn(componentName, message) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  console.warn("[TDS] ".concat(componentName, ": ").concat(message)); // eslint-disable-line no-console
};

var preventDisabling = function preventDisabling(_ref) {
  var disabled = _ref.disabled,
      props = objectWithoutProperties(_ref, ["disabled"]);

  if (disabled) {
    warn('Button', 'Buttons are not able to be disabled.');
  }

  return props;
};

var baseButton = _extends_1({
  margin: 0,
  padding: '0 2rem',
  cursor: 'pointer',
  background: 'none',
  transition: 'background 0.2s',
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center'
}, media.from('md').css({
  display: 'inline-flex',
  width: 'auto',
  minWidth: '180px'
}));

var StyledButton = styled.button.withConfig({
  displayName: "Button__StyledButton",
  componentId: "sa7xo3-0"
})(borders.none, borders.rounded, medium, boldFont, forms.buttonHeight, forms.font, baseButton, function (_ref2) {
  var variant = _ref2.variant;
  var backgroundColor;
  var color;
  var hover = {
    boxShadow: '0 0 0 1px'
  };

  if (variant === 'primary') {
    backgroundColor = colorPrimary;
    color = colorWhite;
    hover.backgroundColor = colorWhite;
    hover.color = colorPrimary;
  } else if (variant === 'secondary') {
    backgroundColor = colorSecondary;
    color = colorWhite;
    hover.backgroundColor = colorWhite;
    hover.color = colorSecondary;
  } else {
    backgroundColor = colorWhite;
    color = colorText;
    hover.backgroundColor = 'transparent';
    hover.color = colorWhite;
  }

  return {
    backgroundColor: backgroundColor,
    color: color,
    '&:hover': hover
  };
});
var ButtonTextWrapper = styled.span.withConfig({
  displayName: "Button__ButtonTextWrapper",
  componentId: "sa7xo3-1"
})({
  width: '100%'
});
/**
 * @version ./package.json
 */

var Button = forwardRef(function (_ref3, ref) {
  var type = _ref3.type,
      variant = _ref3.variant,
      children = _ref3.children,
      rest = objectWithoutProperties(_ref3, ["type", "variant", "children"]);

  var restNoDisabled = preventDisabling(rest);
  return React.createElement(StyledButton, _extends_1({}, safeRest(restNoDisabled), {
    variant: variant,
    type: type,
    ref: ref
  }), React.createElement(ButtonTextWrapper, null, children));
});
Button.displayName = 'Button';
Button.propTypes = {
  /**
   * The HTML button type.
   */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),

  /**
   * The style.
   */
  variant: PropTypes.oneOf(['primary', 'secondary', 'inverted']),

  /**
   * The label. It can include the `A11yContent` component, strings, or strings wrapped in a `<span>`.
   */
  children: or([PropTypes.string, componentWithName('A11yContent'), htmlElement('span')]).isRequired
};
Button.defaultProps = {
  type: 'button',
  variant: 'primary'
};

export default Button;
export { ButtonTextWrapper, StyledButton };
